import { cn } from '@/lib/utils';

interface GlassCompatibleBackgroundProps {
  /**
   * The color at the top edge of the gradient.
   * This is what iOS 26 Safari samples for the glass effect.
   * Should be an opaque color (no transparency).
   */
  topColor: string;
  
  /**
   * Optional bottom color for the gradient.
   * Defaults to black (#000).
   */
  bottomColor?: string;
  
  /**
   * Optional CSS gradient stop for where the top color transitions.
   * Defaults to '0%' (immediate transition point).
   */
  topColorStop?: string;
  
  /**
   * Optional CSS gradient stop for where the bottom color starts.
   * Defaults to '100%' for a full gradient.
   */
  bottomColorStop?: string;
  
  /**
   * Additional class names.
   */
  className?: string;
  
  /**
   * Optional children to render inside the background layer.
   */
  children?: React.ReactNode;
}

/**
 * A background component optimized for iOS 26 Safari's "Liquid Glass" effect.
 * 
 * Safari 26 samples the top edge of the viewport to tint the status bar and URL bar.
 * This component provides a fixed full-screen gradient with an opaque top color
 * that Safari can sample.
 * 
 * Usage:
 * ```tsx
 * <GlassCompatibleBackground topColor={extractedColor || '#1a1a2e'} />
 * ```
 */
export function GlassCompatibleBackground({
  topColor,
  bottomColor = '#000000',
  topColorStop = '0%',
  bottomColorStop = '100%',
  className,
  children,
}: GlassCompatibleBackgroundProps) {
  return (
    <div
      className={cn('fixed inset-0 -z-10', className)}
      style={{
        background: `linear-gradient(180deg, ${topColor} ${topColorStop}, ${bottomColor} ${bottomColorStop})`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * A variant that creates a more complex gradient with a mid-point.
 * Useful for pages with dynamic color extraction where you want
 * the color to fade through a transition.
 */
export function GlassCompatibleGradient({
  topColor,
  midColor,
  bottomColor = '#000000',
  className,
  children,
}: {
  topColor: string;
  midColor?: string;
  bottomColor?: string;
  className?: string;
  children?: React.ReactNode;
}) {
  const gradient = midColor
    ? `linear-gradient(180deg, ${topColor} 0%, ${midColor} 40%, ${bottomColor} 100%)`
    : `linear-gradient(180deg, ${topColor} 0%, ${bottomColor} 100%)`;

  return (
    <div
      className={cn('fixed inset-0 -z-10', className)}
      style={{ background: gradient }}
    >
      {children}
    </div>
  );
}
